<script setup lang="ts">
import type { CurrencyCode, MeasureUnit } from '~/store/preferences';
import { PREFERRED_CURRENCIES } from '~/constants/currency';
import type { LocaleCode } from '~/constants/i18n';
import { getLocaleMeta } from '~/utils/i18n/locale';

const emit = defineEmits<{
  (e: 'locale-picked'): void;
}>();

const { t } = useI18n();
const prefsStore = usePreferencesStore();

const currencyOptions: CurrencyCode[] = [...PREFERRED_CURRENCIES];
const measureOptions: { value: MeasureUnit; label: string }[] = [
  { value: 'ft2', label: 'SQF' },
  { value: 'm2', label: 'SQM' },
];

// Language is hidden unless NUXT_PUBLIC_ENABLED_LOCALES enables more than English, and on staff paths.
const { enabledLocales, currentLocale, canSwitchLocale, switchLocale } = useLocaleSwitch();
const pickLocale = (code: LocaleCode) => {
  emit('locale-picked');
  switchLocale(code);
};

const optionClass = (active: boolean) =>
  active ? 'bg-white text-black-100 shadow-[0_1px_3px_rgba(13,15,21,0.06)]' : 'text-black-60 hover:text-black-100';

const LABEL_CLASS = 'font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-black-60';
</script>

<template>
  <div
    class="flex flex-col gap-3 border-b border-black/[0.08] px-4 py-3"
    data-testid="top-nav-preferences"
  >
    <div v-if="canSwitchLocale">
      <p :class="LABEL_CLASS">{{ t('common.preferences.language') }}</p>
      <div class="mt-1.5 flex flex-wrap gap-1 rounded-lg bg-black-03 p-1">
        <button
          v-for="code in enabledLocales"
          :key="code"
          type="button"
          class="flex-1 whitespace-nowrap rounded-md px-2 py-1.5 text-[12px] font-semibold transition-colors"
          :class="optionClass(currentLocale === code)"
          :lang="getLocaleMeta(code).language"
          @click="pickLocale(code)"
        >
          {{ getLocaleMeta(code).name }}
        </button>
      </div>
    </div>
    <div>
      <p :class="LABEL_CLASS">{{ t('common.preferences.currency') }}</p>
      <div class="mt-1.5 flex gap-1 rounded-lg bg-black-03 p-1">
        <button
          v-for="code in currencyOptions"
          :key="code"
          type="button"
          class="flex-1 rounded-md py-1.5 text-[12px] font-semibold transition-colors"
          :class="optionClass(prefsStore.currency === code)"
          @click="prefsStore.setCurrency(code)"
        >
          {{ code }}
        </button>
      </div>
    </div>
    <div>
      <p :class="LABEL_CLASS">{{ t('nav.topNav.unitLabel') }}</p>
      <div class="mt-1.5 flex gap-1 rounded-lg bg-black-03 p-1">
        <button
          v-for="opt in measureOptions"
          :key="opt.value"
          type="button"
          class="flex-1 rounded-md py-1.5 text-[12px] font-semibold transition-colors"
          :class="optionClass(prefsStore.measureUnit === opt.value)"
          @click="prefsStore.setMeasureUnit(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>
  </div>
</template>
